import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "component/ui/Dialog";
import { Button } from "component/ui/Button";
import CopyButton from "component/ui/CopyButton";
import { Loader2, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

/**
 * WaitingToConfirmTransferModal
 * Unified waiting / confirmation transfer modal:
 * - Collapsed state: "It’s taking longer than expected" + awaiting transfer card + "Show account details"
 * - Expanded state: "We’re waiting to confirm transfer" + Amount card + "Hide account details" + full bank details
 */
const WaitingToConfirmTransferModal = ({
  isOpen = false,
  onClose,
  amount = "₦0",
  bankName = "",
  accountNumber = "",
  accountName = "",
  narration = "",
  isCrypto = false,
  network = "",
  address = "",
  initialShowDetails = false,
  isConfirmed = false,
  onDone,
  onNeedHelp,
}) => {
  const [showDetails, setShowDetails] = useState(initialShowDetails);

  useEffect(() => {
    if (isOpen) {
      setShowDetails(initialShowDetails);
    }
  }, [isOpen, initialShowDetails]);

  if (!isOpen) return null;

  const displayNetwork = network || (isCrypto ? bankName : "");
  const displayAddress = address || (isCrypto ? accountNumber : "");
  const displayBankName = !isCrypto ? bankName : "";
  const displayAccountNumber = !isCrypto ? accountNumber : "";
  const cleanAmount = amount.replace(/[^0-9.]/g, "") || amount;

  const handleDoneClick = () => {
    if (!isConfirmed) return;
    if (onDone) {
      onDone();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
      <DialogContent
        showCloseButton={true}
        className="max-w-[560px] w-full rounded-[16px] bg-white shadow-2xl overflow-hidden border-0"
      >
        <div className="p-6 sm:p-8 pb-0">
          {/* Header */}
          <div className="flex flex-col items-center text-center">
            <DialogTitle className="text-xl sm:text-2xl font-semibold text-title-dark tracking-tight">
              {"We’re waiting to confirm transfer"}
            </DialogTitle>
            {!showDetails ? (
              <DialogDescription className="text-sm text-slate-text font-[350] leading-relaxed px-1 mx-auto mt-1">
                You can leave this page. You’ll be notified once your payment is confirmed.
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">
                Transfer confirmation in progress
              </DialogDescription>
            )}
          </div>

          {/* Body Content */}
          {!showDetails ? (
            /* Collapsed State: Awaiting transfer with Show account details */
            <div>
              <div className="w-full bg-grey-bg rounded-lg mt-5 overflow-hidden">
                <div className="py-5 px-6 flex items-center justify-center gap-2">
                  <Loader2 className="size-4 animate-spin text-label-text" />
                  <span className="text-sm font-normal text-label-text">Awaiting transfer</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="w-full py-3.5 px-6 flex items-center justify-center gap-1.5 text-sm font-medium text-main-dark hover:text-black transition-colors cursor-pointer bg-transparent border-0"
              >
                <span>{isCrypto ? "Show wallet details" : "Show account details"}</span>
                <ChevronDown size={16} />
              </button>
            </div>
          ) : (
            /* Expanded State: Amount card + Hide toggle + Account details card */
            <div className="mt-5 space-y-3">
              {/* Amount Card */}
              <div className="w-full bg-grey-bg rounded-lg py-4 px-6 flex flex-col items-center justify-center gap-1.5">
                <span className="text-xs sm:text-sm font-normal text-label-text">
                  Amount to send
                </span>

                <div className="flex items-center justify-center gap-2">
                  <span className="text-3xl sm:text-[32px] font-medium text-main-dark tracking-tight font-sans">
                    {amount}
                  </span>
                  <CopyButton
                    text={cleanAmount}
                    toastMessage="Amount copied"
                    size={15}
                    title="Copy amount"
                  />
                </div>

                <div className="flex items-center gap-2 text-xs sm:text-sm text-label-text font-[400] mt-0.5">
                  <Loader2 className="size-3.5 animate-spin text-label-text" />
                  <span>Awaiting transfer</span>
                </div>
              </div>

              {/* Accordion Toggle (Hide account details) */}
              <div className="py-1 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="flex items-center gap-1 text-sm font-medium text-main-dark hover:text-black transition-colors cursor-pointer bg-transparent border-0 p-0"
                >
                  <span>{isCrypto ? "Hide wallet details" : "Hide account details"}</span>
                  <ChevronUp size={16} />
                </button>
              </div>

              {/* Collapsible Account Details Card */}
              <div className="w-full bg-grey-bg rounded-[8px] p-4 sm:p-5 space-y-3.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-label-text font-normal">Amount to send</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium font-mono text-sm text-gray-90">{amount}</span>
                    <CopyButton
                      text={cleanAmount}
                      toastMessage="Amount copied"
                      size={15}
                      title="Copy amount"
                    />
                  </div>
                </div>
                <div className="border-t border-x-0 border-b-0 border-solid border-secondary-50 mb-3" />

                {isCrypto ? (
                  <>
                    {/* Network */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-label-text font-normal">Network</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-90">{displayNetwork || "—"}</span>
                        {displayNetwork && (
                          <CopyButton
                            text={displayNetwork}
                            toastMessage="Network copied"
                            size={15}
                            title="Copy network"
                          />
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-label-text font-normal">Address</span>
                      <div className="flex items-center gap-2 max-w-[70%]">
                        <span className="font-medium text-gray-90 truncate font-mono text-xs sm:text-sm">
                          {displayAddress || "—"}
                        </span>
                        {displayAddress && (
                          <CopyButton
                            text={displayAddress}
                            toastMessage="Address copied"
                            size={15}
                            title="Copy address"
                          />
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Bank name */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-label-text font-normal">Bank name</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-90">{displayBankName || "—"}</span>
                        {displayBankName && (
                          <CopyButton
                            text={displayBankName}
                            toastMessage="Bank name copied"
                            size={15}
                            title="Copy bank name"
                          />
                        )}
                      </div>
                    </div>

                    {/* Account number */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-label-text font-normal">Account number</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-90">{displayAccountNumber || "—"}</span>
                        {displayAccountNumber && (
                          <CopyButton
                            text={displayAccountNumber}
                            toastMessage="Account number copied"
                            size={15}
                            title="Copy account number"
                          />
                        )}
                      </div>
                    </div>

                    {/* Account name */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-label-text font-normal">Account name</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-90 text-right">{accountName || "—"}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-solid border-card-bg flex p-6 sm:py-6 sm:px-8 items-center justify-between w-full">
          {/* Need help? link */}
          <button
            type="button"
            onClick={onNeedHelp}
            className="flex items-center gap-2 text-sm font-medium text-[#62748E] hover:text-main-dark transition-colors cursor-pointer bg-transparent border-0 p-0"
          >
            <HelpCircle size={17} className="text-[#62748E]" />
            <span>Need help?</span>
          </button>

          {/* Done button */}
          <Button
            type="button"
            onClick={handleDoneClick}
            disabled={!isConfirmed}
            className="!bg-btn-dark hover:!bg-btn-dark/80 text-white font-medium text-sm rounded-[8px] px-6 h-11 transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaitingToConfirmTransferModal;
